import React, { useRef, useEffect, useState, useCallback } from 'react';
import API from '../../../api/Api';
import ConfettiAnimation from '../shared/ConfettiAnimation';

const ScratchCard: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isScratching, setIsScratching] = useState(false);

  const [prize, setPrize] = useState('');
  const [gameComplete, setGameComplete] = useState(false);
  const [paymentCount, setPaymentCount] = useState(0);
  const [canPlay, setCanPlay] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showConfetti, setShowConfetti] = useState(false);

  const checkPaymentHistory = useCallback(async () => {
    try {
      const prizes = ['🎁 Geschenk!', '💰 10€ Gutschein!', '🎉 Bonus Punkte!', '⭐ Überraschung!', '🎊 Jackpot!'];
      const history = await API.getCustomerHistory();
      const historyArray = Array.isArray(history) ? history : [];
      const payments = historyArray.filter((item: any) => 
        item.interaction?.interactionType === 'PAYMENT_INTERACTION'
      );
      const count = payments.length;
      setPaymentCount(count);
      setCanPlay(count >= 3);
      
      if (count >= 3) {
        initializeCard();
        setPrize(prizes[Date.now() % prizes.length]);
      }
    } catch (error) {
      console.error('Error fetching customer history:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkPaymentHistory();
  }, [checkPaymentHistory]);

  const initializeCard = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = 300;
    canvas.height = 200;

    ctx.fillStyle = '#c0c0c0';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.fillStyle = '#a0a0a0';
    for (let i = 0; i < canvas.width; i += 20) {
      for (let j = 0; j < canvas.height; j += 20) {
        if ((i + j) % 40 === 0) {
          ctx.fillRect(i, j, 10, 10);
        }
      }
    }

    ctx.fillStyle = '#666';
    ctx.font = '16px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('Rubbel mich!', canvas.width / 2, canvas.height / 2);
  };

  const getMousePos = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };

  const scratch = (x: number, y: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(x, y, 20, 0, 2 * Math.PI);
    ctx.fill();

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const pixels = imageData.data;
    let transparent = 0;

    for (let i = 3; i < pixels.length; i += 4) {
      if (pixels[i] === 0) transparent++;
    }

    const percentage = (transparent / (pixels.length / 4)) * 100;

    if (percentage > 50 && !gameComplete) {
      setGameComplete(true);
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 3000);
    }
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!canPlay) return;
    setIsScratching(true);
    const pos = getMousePos(e);
    scratch(pos.x, pos.y);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isScratching || !canPlay) return;
    const pos = getMousePos(e);
    scratch(pos.x, pos.y);
  };

  const handleMouseUp = () => {
    setIsScratching(false);
  };



  const renderStatusMessage = () => {
    if (loading) {
      return <p style={{ fontSize: '16px', color: '#666' }}>Lade...</p>;
    }
    
    if (canPlay) {
      return (
        <p style={{ fontSize: '16px', color: '#666' }}>
          Rubbel die graue Fläche weg, um deinen Preis zu enthüllen!
        </p>
      );
    }
    
    const remainingOrders = 3 - paymentCount;
    return (
      <p style={{ fontSize: '16px', color: '#856404', fontWeight: 'bold', margin: 0 }}>
        🛒 Noch {remainingOrders} Bestellung{remainingOrders === 1 ? '' : 'en'} bis zum Rubbel-Los!
      </p>
    );
  };

  return (
    <div style={{
      padding: '20px',
      textAlign: 'center',
      position: 'relative',
      backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.7), rgba(255, 255, 255, 0.7)), url(/gameZoneImages/memoryGame/memory_back.jpg)',
      backgroundSize: 'contain',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      backgroundAttachment: 'fixed',
      minHeight: '80vh',
      overflow: 'hidden'
    }}>
      <ConfettiAnimation show={showConfetti} />
      <div style={{ marginBottom: '20px' }}>
        <h3 style={{ margin: '0 0 10px 0' }}>Rubbel-Los</h3>
        {renderStatusMessage()}
      </div>

      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '20px'
      }}>
        <div style={{
          position: 'relative',
          border: '3px solid #d4af37',
          borderRadius: '10px',
          padding: '20px',
          backgroundColor: '#fff',
          opacity: canPlay ? 1 : 0.5
        }}>
          <div style={{
            position: 'absolute',
            top: '20px',
            left: '20px',
            width: '300px',
            height: '200px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px',
            fontWeight: 'bold',
            color: '#333',
            backgroundColor: '#f8f9fa',
            borderRadius: '5px',
            zIndex: 0
          }}>
            {prize}
          </div>
          <canvas
            ref={canvasRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            style={{
              cursor: 'pointer',
              borderRadius: '5px',
              position: 'relative',
              zIndex: 1,
              margin: '20px'
            }}
          />
        </div>

        {gameComplete && (
          <div style={{
            padding: '15px',
            backgroundColor: '#4d943a',
            color: 'white',
            borderRadius: '8px',
            fontSize: '18px',
            fontWeight: 'bold'
          }}>
            🎉 Gewonnen: {prize}
          </div>
        )}


      </div>
    </div>
  );
};

export default ScratchCard;